import { useCallback, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { toastManager, type EntitySelection } from "@salestrak/ui";
import { fieldDto } from "@salestrak/custom-fields";
import { CreateIssueDocument, type EntityType, type IssueRow } from "#app/lib/data";
import { insertIssueIntoStageCache } from "#app/hooks/kanban/kanban-cache-utils";
import { isForbiddenError } from "#app/lib/utils/is-forbidden-error";
import { toastMutationError } from "#app/lib/utils/toast-errors";
import type { CustomFieldDef } from "#app/components/issue-view/issue-view-types";
import { useAnalytics, AnalyticsContext, IssueEvent } from "#app/lib/analytics";
import type { Omittable } from "@salestrak/ui";

type CreateIssueDraft = Omittable<{
  name: string;
  description?: string;
  issueTypeId: string | null;
  workflowId: string;
  stageId: string | null;
  issueSourceId?: string | null;
  address: Omittable<{ wholeAddress?: string; latLng?: { lat: number; lng: number } }> | null;
  assignees: EntitySelection[];
  values: Record<string, unknown>;
}>;

type UseCreateIssueMutationOptions = Omittable<{
  onSuccess?: (issue: IssueRow) => void;
  /** Where the create flow was launched from — included in analytics. */
  source?: string;
}>;

export function useCreateIssueMutation({ onSuccess, source }: UseCreateIssueMutationOptions = {}) {
  const [createIssueMutation] = useMutation(CreateIssueDocument);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { track } = useAnalytics();

  const createIssue = useCallback(
    async (draft: CreateIssueDraft, customFieldDefs: Record<string, CustomFieldDef>) => {
      if (!draft.name.trim()) return;

      setIsSubmitting(true);
      try {
        // Normalize custom field values through fieldDto
        const normalizedValues: Record<string, unknown> = {};
        for (const [cfId, value] of Object.entries(draft.values)) {
          const field = customFieldDefs[cfId];
          if (!field || value === undefined || value === null) continue;
          try {
            normalizedValues[cfId] = fieldDto(field.type, value);
          } catch {
            normalizedValues[cfId] = value;
          }
        }

        const input = {
          name: draft.name.trim(),
          description: draft.description || undefined,
          issueTypeId: draft.issueTypeId || undefined,
          stageId: draft.stageId || undefined,
          workflowId: draft.workflowId,
          issueSourceId: draft.issueSourceId || undefined,
          address: draft.address?.wholeAddress || draft.address?.latLng
            ? {
                wholeAddress: draft.address?.wholeAddress || undefined,
                latLng: draft.address?.latLng ?? undefined,
              }
            : undefined,
          assignees:
            draft.assignees.length > 0
              ? draft.assignees.map((s, index) => ({
                  entityType: ((s.entity.data?.entityType as string) ?? "user") as EntityType,
                  entityId: s.id,
                  isPrimary: index === 0,
                }))
              : undefined,
          values:
            Object.keys(normalizedValues).length > 0
              ? normalizedValues
              : undefined,
        };

        const { data, error } = await createIssueMutation({
          variables: { input },
          update: (cache, { data: result }) => {
            if (!result?.createIssue) return;
            const created = result.createIssue;
            if ("issueId" in created && created.issueId && draft.stageId) {
              insertIssueIntoStageCache(cache, draft.stageId, {
                issueId: created.issueId as string,
                sortOrder: Number.MIN_SAFE_INTEGER,
              });
            }
          },
        });

        // Apollo client uses errorPolicy: "all" globally, so top-
        // level GraphQL errors land in the result `error` instead of throwing.
        // The global error link already toasts for 403/forbidden;
        // skip the generic failure toast in that case.
        if (!data?.createIssue) {
          if (!isForbiddenError(error)) {
            toastManager.add({ title: "Failed to create project", type: "error" });
          }
          return;
        }

        const result = data.createIssue;
        if ("__typename" in result) {
          if (result.__typename === "ValidationError" || result.__typename === "PermissionDenied") {
            toastManager.add({ title: "message" in result ? (result.message as string) : "Failed to create project", type: "error" });
            return;
          }
        }

        toastManager.add({ title: "Project created", type: "success" });
        if ("issueId" in result) {
          track({
            context: AnalyticsContext.Issue,
            event: IssueEvent.CreateFinish,
            data: { source: source ?? "unknown" },
          });
          onSuccess?.(result as unknown as IssueRow);
        }
      } catch (err) {
        toastMutationError(err, "Failed to create project");
      } finally {
        setIsSubmitting(false);
      }
    },
    [createIssueMutation, onSuccess, source, track],
  );

  return { createIssue, isSubmitting };
}
